import type { ProfilerOnRenderCallback } from "react";
import { restartReact } from "./reactRoot";

/**
 * Length of the rolling window in which React commits are counted.
 *
 * A two-second window is long enough to distinguish a sustained render storm from a
 * short, legitimate burst caused by opening a complex pane. Increasing this value makes
 * the watchdog react more slowly and includes more historical commits in the count.
 * Decreasing it makes detection faster, but also more sensitive to brief UI activity.
 */
const COMMIT_WINDOW_MS = 2_000;

/**
 * Number of commits inside COMMIT_WINDOW_MS from which activity is classified as a storm.
 *
 * This corresponds to 75 commits per second, which interactive plans can legitimately
 * exceed. A sustained high rate is diagnostic evidence only. Recovery additionally
 * requires a measured main-thread delay after that sustained storm.
 */
const COMMIT_LIMIT = 150;

/**
 * Minimum time for which the commit rate must continuously remain above COMMIT_LIMIT.
 *
 * The duration resets whenever the rolling count drops below the limit, including
 * periods without React commits. A storm alone never requests a restart.
 */
const STORM_DURATION_MS = 5_000;

/**
 * Interval of the event-loop heartbeat used to detect a blocked browser main thread.
 *
 * The heartbeat itself performs only constant-time bookkeeping. A shorter interval gives
 * more precise lag measurements but wakes the browser more frequently. A longer interval
 * reduces wake-ups but makes detection less precise and slightly slower.
 */
const HEARTBEAT_MS = 500;

/**
 * Minimum heartbeat delay that is considered a serious main-thread blockage.
 *
 * A lag event does not trigger recovery by itself because imports, PDF processing, browser
 * throttling, and debugging pauses can all block the main thread legitimately. Recovery is
 * only requested when this lag can also be correlated with a recently detected commit
 * storm. The measured value excludes the normal HEARTBEAT_MS interval.
 */
const MAIN_THREAD_LAG_MS = 3_000;

/**
 * Quiet period between unmounting the damaged React root and mounting the new root.
 *
 * This gives pending browser work, cleanup callbacks, and queued tasks a brief opportunity
 * to settle. Global application state remains available during this delay. Local component
 * and hook state is discarded by the unmount regardless of this value.
 */
const RESTART_DELAY_MS = 500;

/**
 * Minimum distance between two automatic restart attempts.
 *
 * The cooldown prevents a persistent underlying producer from causing a rapid sequence of
 * unmount/mount cycles. It is measured with performance.now(), so it applies to the current
 * page lifetime and is unaffected by changes to the system clock.
 */
const RESTART_COOLDOWN_MS = 30_000;

/**
 * Hard limit for automatic React-root restarts during one browser-tab session.
 *
 * Once this limit is reached, automatic recovery is disabled and an error is written to the
 * console. This prevents an unrecoverable defect from trapping the application in a restart
 * loop. The counter is stored in sessionStorage, so remounting React does not reset it; a
 * new browser-tab session normally does.
 */
const MAX_RESTARTS_PER_SESSION = 5;

/** sessionStorage key holding the number of automatic restarts in the current tab session. */
const RESTART_COUNT_KEY = "reactWatchdogRestartCount";

/**
 * sessionStorage key holding the diagnostic snapshot captured immediately before recovery.
 * The latest snapshot replaces the previous one and survives the React-root remount.
 */
const DIAGNOSTIC_KEY = "lastReactRecovery";

/**
 * Capacity of the fixed commit timestamp ring buffer.
 *
 * It must be larger than COMMIT_LIMIT so the threshold can be evaluated without losing
 * relevant timestamps. A fixed Float64Array avoids allocating objects on every production
 * commit. If COMMIT_LIMIT is raised above this value, this capacity must be raised as well.
 */
const COMMIT_BUFFER_SIZE = 512;

type RecoveryReason = "main-thread-lag-after-commit-storm";

type WatchdogState = {
  commits: Float64Array;
  writeIndex: number;
  commitCount: number;
  recentCommitCount: number;
  stormStartedAt: number;
  lastStormCommitAt: number;
  lastRestartAt: number;
  recoveryScheduled: boolean;
  started: boolean;
  disabled: boolean;
  heartbeatId?: number;
  stormReported: boolean;
  lastInput?: { type: string; at: number; timestamp: string };
  heartbeatSamples: Array<{ at: number; lagMs: number }>;
};

const watchdog: WatchdogState = {
  commits: new Float64Array(COMMIT_BUFFER_SIZE),
  writeIndex: 0,
  commitCount: 0,
  recentCommitCount: 0,
  stormStartedAt: 0,
  lastStormCommitAt: 0,
  lastRestartAt: Number.NEGATIVE_INFINITY,
  recoveryScheduled: false,
  started: false,
  disabled: false,
  stormReported: false,
  heartbeatSamples: [],
};

function sessionRestartCount() {
  try {
    return Number(sessionStorage.getItem(RESTART_COUNT_KEY) || 0);
  } catch {
    return 0;
  }
}

function resetStorm() {
  watchdog.stormStartedAt = 0;
  watchdog.lastStormCommitAt = 0;
  watchdog.stormReported = false;
}

function resetCommitWindow() {
  watchdog.commits.fill(0);
  watchdog.writeIndex = 0;
  watchdog.commitCount = 0;
  watchdog.recentCommitCount = 0;
  resetStorm();
}

function responsivenessDiagnostic(now: number) {
  return {
    lastInput: watchdog.lastInput ? { type: watchdog.lastInput.type, timestamp: watchdog.lastInput.timestamp, ageMs: now - watchdog.lastInput.at } : undefined,
    visibilityState: document.visibilityState,
    heartbeatIntervalMs: HEARTBEAT_MS,
    mainThreadLagThresholdMs: MAIN_THREAD_LAG_MS,
    heartbeatSamples: watchdog.heartbeatSamples.map(sample => ({ ageMs: now - sample.at, lagMs: sample.lagMs })),
  };
}

function saveDiagnostic(reason: RecoveryReason, lagMs?: number) {
  const restartAttempt = sessionRestartCount() + 1;
  const diagnostic = {
    timestamp: new Date().toISOString(),
    reason,
    recentCommitCount: watchdog.recentCommitCount,
    commitWindowMs: COMMIT_WINDOW_MS,
    stormDurationMs: watchdog.stormStartedAt ? watchdog.lastStormCommitAt - watchdog.stormStartedAt : 0,
    lagMs,
    sustainedStormDurationMs: watchdog.stormStartedAt ? watchdog.lastStormCommitAt - watchdog.stormStartedAt : 0,
    ...responsivenessDiagnostic(performance.now()),
    restartAttempt,
    activePaneId: (() => {
      try {
        return globalThis.state?.activePaneId;
      } catch {
        return undefined;
      }
    })(),
  };

  try {
    sessionStorage.setItem(DIAGNOSTIC_KEY, JSON.stringify(diagnostic));
    sessionStorage.setItem(RESTART_COUNT_KEY, String(restartAttempt));
  } catch {
    // Recovery must not depend on storage availability.
  }
  return diagnostic;
}

function scheduleRecovery(reason: RecoveryReason, lagMs?: number) {
  const now = performance.now();
  if (watchdog.disabled || watchdog.recoveryScheduled || now - watchdog.lastRestartAt < RESTART_COOLDOWN_MS) {
    return;
  }

  if (sessionRestartCount() >= MAX_RESTARTS_PER_SESSION) {
    watchdog.disabled = true;
    console.error("[ReactWatchdog] automatic recovery disabled after five restarts", {
      lastRecovery: sessionStorage.getItem(DIAGNOSTIC_KEY),
    });
    return;
  }

  watchdog.recoveryScheduled = true;
  watchdog.lastRestartAt = now;
  const diagnostic = saveDiagnostic(reason, lagMs);
  console.error("[ReactWatchdog] restarting React", diagnostic);

  // Leave the current Profiler/commit callback before starting recovery. A microtask gets
  // priority over another scheduled React task, while still avoiding a synchronous unmount.
  queueMicrotask(() => {
    console.error("[ReactWatchdog] restarting React - 1", diagnostic);
    restartReact(RESTART_DELAY_MS)
      .then(() => {
        resetCommitWindow();
        watchdog.recoveryScheduled = false;
      })
      .catch((error) => {
        watchdog.recoveryScheduled = false;
        console.error("[ReactWatchdog] React restart failed", error);
      });
  });
}

function countRecentCommits(now: number) {
  const stored = Math.min(watchdog.commitCount, COMMIT_BUFFER_SIZE);
  let recent = 0;
  for (let index = 0; index < stored; index++) {
    if (now - watchdog.commits[index] <= COMMIT_WINDOW_MS) recent++;
  }
  return recent;
}

/** Expire the rolling window even when React is quiet. */
function refreshStorm(now: number, committed = false) {
  watchdog.recentCommitCount = countRecentCommits(now);
  if (watchdog.recentCommitCount < COMMIT_LIMIT) {
    resetStorm();
    return;
  }
  if (!watchdog.stormStartedAt) watchdog.stormStartedAt = now;
  if (committed) watchdog.lastStormCommitAt = now;
  if (!watchdog.stormReported && watchdog.lastStormCommitAt - watchdog.stormStartedAt >= STORM_DURATION_MS) {
    watchdog.stormReported = true;
    console.warn("[ReactWatchdog] sustained commit storm; monitoring responsiveness", {
      timestamp: new Date().toISOString(),
      recentCommitCount: watchdog.recentCommitCount,
      commitWindowMs: COMMIT_WINDOW_MS,
      stormDurationMs: watchdog.lastStormCommitAt - watchdog.stormStartedAt,
      ...responsivenessDiagnostic(now),
    });
  }
}

/** Commit frequency alone is never sufficient evidence for restarting an interactive UI. */
export const onReactRender: ProfilerOnRenderCallback = () => {
  if (watchdog.disabled || watchdog.recoveryScheduled || document.visibilityState === "hidden") return;
  const now = performance.now();
  // Expire old evidence before a new commit can bring the count back above the limit.
  refreshStorm(now);
  watchdog.commits[watchdog.writeIndex] = now;
  watchdog.writeIndex = (watchdog.writeIndex + 1) % COMMIT_BUFFER_SIZE;
  watchdog.commitCount++;
  refreshStorm(now, true);
};

export function startReactWatchdog() {
  if (watchdog.started) return;
  watchdog.started = true;
  let expectedHeartbeat = performance.now() + HEARTBEAT_MS;

  const recordInput = (event: Event) => {
    if (!event.isTrusted) return;
    watchdog.lastInput = { type: event.type, at: performance.now(), timestamp: new Date().toISOString() };
  };
  for (const type of ["pointermove", "pointerdown", "pointerup", "click", "wheel", "keydown"]) {
    window.addEventListener(type, recordInput, { capture: true, passive: true });
  }
  // Background throttling is not evidence that React is broken. Start fresh on return.
  document.addEventListener("visibilitychange", () => {
    resetCommitWindow();
    watchdog.heartbeatSamples = [];
    expectedHeartbeat = performance.now() + HEARTBEAT_MS;
  });

  watchdog.heartbeatId = window.setInterval(() => {
    const now = performance.now();
    const lagMs = Math.max(0, now - expectedHeartbeat);
    expectedHeartbeat = now + HEARTBEAT_MS;
    if (watchdog.disabled || watchdog.recoveryScheduled || document.visibilityState === "hidden") return;
    watchdog.heartbeatSamples.push({ at: now, lagMs });
    if (watchdog.heartbeatSamples.length > 20) watchdog.heartbeatSamples.shift();

    // Only time actually observed above the commit threshold qualifies; the blocked
    // interval itself must not turn a brief burst into a five-second storm.
    const sustainedStorm = watchdog.stormStartedAt > 0 &&
      watchdog.lastStormCommitAt - watchdog.stormStartedAt >= STORM_DURATION_MS;
    const stormImmediatelyBeforeLag = watchdog.lastStormCommitAt > 0 &&
      now - watchdog.lastStormCommitAt <= lagMs + COMMIT_WINDOW_MS;
    if (lagMs >= MAIN_THREAD_LAG_MS && sustainedStorm && stormImmediatelyBeforeLag) {
      scheduleRecovery("main-thread-lag-after-commit-storm", lagMs);
    }
    if (!watchdog.recoveryScheduled) refreshStorm(now);
  }, HEARTBEAT_MS);
}

export function getReactWatchdogStatus() {
  return {
    recentCommitCount: watchdog.recentCommitCount,
    stormDurationMs: watchdog.stormStartedAt ? watchdog.lastStormCommitAt - watchdog.stormStartedAt : 0,
    ...responsivenessDiagnostic(performance.now()),
    recoveryScheduled: watchdog.recoveryScheduled,
    restartCount: sessionRestartCount(),
    disabled: watchdog.disabled,
  };
}

declare global {
  var reactWatchdog: {
    status: typeof getReactWatchdogStatus;
  };
}

globalThis.reactWatchdog = { status: getReactWatchdogStatus };
